import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Send, CheckCircle, Mail, User, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    bookingId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/support/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          booking_id: formData.bookingId || null
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('Failed to submit inquiry. Please try again.');
      }
    } catch (error) {

      alert('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '2rem' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '4rem' }}>
          <Card style={{ textAlign: 'center', padding: '2rem' }}>
            <CheckCircle size={64} color="#10b981" style={{ margin: '0 auto 1rem' }} />
            <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: '#10b981' }}>
              Thank You!
            </h1>
            <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '1rem' }}>
              We've received your inquiry and will get back to you within 24 hours.
            </p>
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '2rem' }}>
              Check your email for a confirmation message.
            </p>
            <Button 
              onClick={() => navigate('/')}
              style={{ 
                padding: '0.75rem 2rem', 
                backgroundColor: '#10b981',
                color: 'white',
                fontSize: '1rem'
              }}
            >
              Back to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 2rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 
            onClick={() => navigate('/')}
            style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              color: '#10b981',
              cursor: 'pointer'
            }}
          >
            ReturnPal
          </h1>
        </div>
      </div>

      {/* Contact Form */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <MessageSquare size={48} color="#10b981" style={{ margin: '0 auto 1rem' }} />
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
            Contact Support
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>
            Have a question or need help? We're here for you!
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Send us a message</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {/* Name */}
                <div>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    <User size={16} style={{ marginRight: '0.5rem' }} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    placeholder="John Doe"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    <Mail size={16} style={{ marginRight: '0.5rem' }} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                {/* Booking ID (Optional) */}
                <div>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    <FileText size={16} style={{ marginRight: '0.5rem' }} />
                    Booking Confirmation Number (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.bookingId}
                    onChange={(e) => setFormData({...formData, bookingId: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    placeholder="e.g., RP-123456"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    Subject *
                  </label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    required
                  >
                    <option value="">Select a topic...</option>
                    <option value="Booking Question">Booking Question</option>
                    <option value="Payment Issue">Payment Issue</option>
                    <option value="Package Status">Package Status</option>
                    <option value="Subscription Management">Subscription Management</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                    placeholder="Please describe your question or concern in detail..."
                    required
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  style={{ 
                    padding: '1rem 2rem', 
                    backgroundColor: isSubmitting ? '#9ca3af' : '#10b981',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    width: '100%'
                  }}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} style={{ marginRight: '0.5rem' }} />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Additional Contact Info */}
        <div style={{ marginTop: '2rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
          <p>
            You can also reach us directly at{' '}
            <a href="mailto:support@getreturnpal.com" style={{ color: '#10b981', fontWeight: '600' }}>
              support@getreturnpal.com
            </a>
          </p>
          <p style={{ marginTop: '0.5rem' }}>We typically respond within 24 hours</p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
